/**
 * Onboarding Modal Component
 * Non-blocking guided tour with action triggers
 * 6-step onboarding: Welcome → Profile → Event → Tour → Equipment → Explore
 */

import { useNavigate } from 'react-router-dom';
import { useOnboarding, ONBOARDING_STEPS } from '@/context/OnboardingContext';
import {
  Sparkles,
  UserCircle,
  Calendar,
  Map,
  Wrench,
  Compass,
  X,
  ChevronRight,
  Check,
} from 'lucide-react';

const STEP_INFO = [
  { id: 'welcome', icon: Sparkles, title: 'Welcome to Music Event Platform', description: 'Your complete tour and event management solution. Let\'s get you started!' },
  { id: 'complete-profile', icon: UserCircle, title: 'Complete Your Profile', description: 'Set up your name, bio, and profile picture so collaborators can find you.' },
  { id: 'create-event', icon: Calendar, title: 'Create Your First Event', description: 'Add a gig, rehearsal, recording session, or any other event. This is where it all begins.' },
  { id: 'create-tour', icon: Map, title: 'Plan Your First Tour', description: 'Organize multiple gigs into a tour with smart routing and financial tracking.' },
  { id: 'add-equipment', icon: Wrench, title: 'Add Your Equipment', description: 'Track your gear inventory with automatic depreciation and availability management.' },
  { id: 'explore-features', icon: Compass, title: 'Explore More Features', description: 'Discover contacts, finances, AI route optimization, audio analysis, and more.' },
];

export function OnboardingModal() {
  const navigate = useNavigate();
  const { isActive, currentStep, completeStep, skip } = useOnboarding();

  if (!isActive || currentStep >= STEP_INFO.length) {
    return null;
  }

  const step = STEP_INFO[currentStep];
  const IconComponent = step.icon;
  const isLastStep = currentStep === STEP_INFO.length - 1;

  const handleNext = () => {
    // Navigate to the next step's route BEFORE completing
    if (!isLastStep) {
      const nextRoute = ONBOARDING_STEPS[currentStep + 1]?.route;
      if (nextRoute) {
        navigate(nextRoute);
      }
    }
    // Then advance the step
    completeStep();
  };

  const handleSkip = () => {
    skip();
  };

  return (
    <div className="fixed bottom-5 right-5 z-[9999] w-[320px] rounded-xl bg-white dark:bg-gray-800 p-5 border border-gray-200 dark:border-gray-700 shadow-xl">
      {/* Close button */}
      <button
        onClick={handleSkip}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Skip onboarding"
      >
        <X size={16} className="text-gray-400" />
      </button>

      {/* Header with icon + progress */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 p-2.5 rounded-full">
          <IconComponent size={24} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
            {step.title}
          </h3>
          {/* Progress dots */}
          <div className="flex gap-1 mt-2">
            {STEP_INFO.map((_, i) => (
              <div
                key={i}
                className="h-1 rounded-full transition-all duration-200"
                style={{
                  flex: i === currentStep ? 2 : 1,
                  backgroundColor: i <= currentStep ? '#2563eb' : '#d1d5db',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
        {step.description}
      </p>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {currentStep + 1}/{STEP_INFO.length}
        </span>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {isLastStep ? (
            <>
              <Check size={14} />
              Finish
            </>
          ) : (
            <>
              {currentStep === 0 ? "Let's Go" : 'Next'}
              <ChevronRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
